import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { FileRequest, PaginatedResponse, RequestStatus } from '@/types/api';

export function useRequests(params: {
  org_id?: string | null;
  status?: RequestStatus | null;
  limit?: number;
  offset?: number;
}) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  return useQuery({
    queryKey: [
      'requests',
      params.org_id ?? null,
      params.status ?? null,
      limit,
      offset,
    ],
    queryFn: async () =>
      (
        await api.get<PaginatedResponse<FileRequest>>('/requests', {
          params: {
            org_id: params.org_id ?? undefined,
            status: params.status ?? undefined,
            limit,
            offset,
          },
        })
      ).data,
  });
}

export function useRequest(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['requests', 'detail', id],
    queryFn: async () => (await api.get<FileRequest>(`/requests/${id}`)).data,
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      organization_id: string;
      title: string;
      instructions?: string;
      target_folder_id?: string;
      due_date?: string;
    }) => {
      const { data } = await api.post<FileRequest>('/requests', vars);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['requests'] }),
  });
}

export function useSubmitRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; file_id: string }) => {
      await api.post(`/requests/${vars.id}/submit`, { file_id: vars.file_id });
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['requests'] });
      qc.invalidateQueries({ queryKey: ['requests', 'detail', v.id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useReviewRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      outcome: 'accepted' | 'rejected';
      note?: string;
    }) => {
      await api.post(`/requests/${vars.id}/review`, {
        outcome: vars.outcome,
        note: vars.note,
      });
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['requests'] });
      qc.invalidateQueries({ queryKey: ['requests', 'detail', v.id] });
    },
  });
}

export function useDeleteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/requests/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['requests'] }),
  });
}
